import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart } from '../../../../types/Chart/Chart';
import useChart from '../../../../hooks/useChart';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { NavForward } from '../../../../components/buttons/NavForward';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { Heading } from '../../../../components/Heading';
import Checkbox from '../../../../components/forms/Checkbox';

export const Route = createFileRoute('/_authenticated/$patientId/education/patientEducation')({
  component: PatientEducationPage,
});

function PatientEducationPage() {
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const [attested, setAttested] = React.useState(false);

  const selfGuidedEducationVideo = document.getElementById('patient-education-video-self') as HTMLVideoElement;

  React.useEffect(() => {
    if (selfGuidedEducationVideo) {
      selfGuidedEducationVideo.load();
    }
  });

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({
        currentPageName: 'patientEducation',
        patientInfo,
        value: true,
      });
    }
  }, [patientInfo]);

  // GET NEXT PAGE URL
  React.useEffect(() => {
    if (chart && patientInfo.navObj) {
      (async () => {
        const awaitedUrl = await getNextPage({
          currentPage: 'patientEducation',
          chart: chart,
          patientInfo: patientInfo,
        });
        setNextPageURL(awaitedUrl);
      })();
    }
  }, [chart, patientInfo]);

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }

  if (patientInfo?.navType.base === 'self' && currentUser?.uid === patientId) {
    return (
      <>
        <div id="general-education-page" className="flex flex-col items-center justify-center">
          <Heading id="general-education-page-header" textContent="General Education Video" />
          <p className="text-xl">Please watch the entire video before continuing</p>
          <div id="general-education-video-container" className="mt-4 flex flex-col items-center">
            {/* TODO: Add captions track and disable warning */}
            {/* eslint-disable-next-line */}
            <video
              id="patient-education-video-self"
              className="w-10/12"
              playsInline={true}
              controls={true}
              // preload="metadata"
            >
              <source
                src="https://storage.googleapis.com/mideoid-console-public/videos/instructional/en/patientEducation.mp4"
                type="video/mp4"
              />
            </video>
          </div>
          <div className="mt-4">
            <Checkbox
              id="attested-watched"
              name="attested-watched"
              onChange={() => {
                setAttested(!attested);
              }}
              checked={attested}
              label="I attest that I have reviewed and understand the contents of the patient education video"
            />
          </div>
          {attested && (
            <div id="nav" className="mt-4 flex flex-col items-center">
              <NavForward
                to={nextPageURL ? nextPageURL : `../../../${patientId}/education/treatmentOption`}
                disabled={false}
              />
            </div>
          )}
        </div>
      </>
    );
  }
  if (patientInfo && currentUser?.uid === patientId) {
    return (
      <>
        <div id="general-education-page" className="flex flex-col items-center justify-center">
          <Heading id="general-education-page-header" textContent="General Education Video" />
          <p className="text-xl">Please watch this informative video before continuing.</p>
          <div id="general-education-video-container" className="mt-4 flex flex-row justify-around">
            {/* TODO: Add captions track and disable warning */}
            {/* eslint-disable-next-line */}
            <video id="patient-education-video" className="w-10/12" controls>
              <source
                src="https://storage.googleapis.com/mideoid-console-public/videos/instructional/en/patientEducation.mp4"
                type="video/mp4"
              />
            </video>
          </div>
          <div id="nav" className="mt-4 flex flex-col items-center">
            {nextPageURL && <NavForward to={nextPageURL} disabled={false} />}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
